import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Loader2, FileText, CheckCircle } from 'lucide-react';
import { z } from 'zod';

const publicPOSchema = z.object({
  district_name: z.string().trim().min(1, "District name is required").max(100, "District name must be less than 100 characters"),
  contact_name: z.string().trim().min(1, "Contact name is required").max(100, "Contact name must be less than 100 characters"),
  contact_email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  contact_phone: z.string().trim().max(20, "Phone number must be less than 20 characters"),
  po_number: z.string().trim().min(1, "PO number is required").max(50, "PO number must be less than 50 characters"),
  po_amount: z.string().trim().min(1, "PO amount is required"),
  comments: z.string().max(1000, "Comments must be less than 1000 characters")
});

interface PublicPOData {
  district_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  po_number: string;
  po_amount: string;
  comments: string;
}

export function PublicPOUpload() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  // Removed AI extraction functionality
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState<PublicPOData>({
    district_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    po_number: '',
    po_amount: '',
    comments: ''
  });

  const handleInputChange = (field: keyof PublicPOData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  // Removed AI extraction functionality

  const uploadFile = async () => {
    if (!file) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `public/${Date.now()}-${file.name}`;

    const { data, error } = await supabase.storage
      .from('public-po-submissions')
      .upload(fileName, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('public-po-submissions')
      .getPublicUrl(fileName);

    return { url: publicUrl, name: file.name };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form data
      const validatedData = publicPOSchema.parse(formData);
      
      // Upload file if provided
      let fileInfo = null;
      if (file) {
        fileInfo = await uploadFile();
      }

      // Convert PO amount to number
      const poAmount = parseFloat(validatedData.po_amount.replace(/[^0-9.-]+/g, ""));

      // Store the PO submission in a public table (we'll need to create this)
      const { error } = await supabase
        .from('public_po_submissions')
        .insert({
          district_name: validatedData.district_name,
          contact_name: validatedData.contact_name,
          contact_email: validatedData.contact_email,
          contact_phone: validatedData.contact_phone,
          po_number: validatedData.po_number,
          po_amount: poAmount,
          comments: validatedData.comments,
          po_file_url: fileInfo?.url,
          po_file_name: fileInfo?.name,
          status: 'pending'
        });

      if (error) throw error;

      setSubmitted(true);
      toast({
        title: "Purchase Order Submitted",
        description: "Thank you! We've received your purchase order and will contact you soon.",
      });

      // Reset form
      setFormData({
        district_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        po_number: '',
        po_amount: '',
        comments: ''
      });
      setFile(null);

    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        toast({
          title: "Validation Error",
          description: "Please fix the errors below",
          variant: "destructive",
        });
      } else {
        console.error('Error submitting PO:', error);
        toast({
          title: "Submission Error",
          description: "Failed to submit purchase order. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h3 className="text-2xl font-bold">Purchase Order Submitted!</h3>
            <p className="text-muted-foreground">
              Thank you for submitting your purchase order. Our team will review it and contact you within 1-2 business days to begin the onboarding process.
            </p>
            <Button 
              onClick={() => setSubmitted(false)}
              variant="outline"
            >
              Submit Another PO
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          <CardTitle className="text-2xl">Submit Purchase Order</CardTitle>
        </div>
        <CardDescription>
          Already have a purchase order? Upload it here and we'll help you get started with Edugogy quickly.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload Section */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="po_file">Upload Purchase Order</Label>
              <Input
                id="po_file"
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
            </div>
          </div>

          {/* District Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">District Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="district_name">District Name *</Label>
                <Input
                  id="district_name"
                  value={formData.district_name}
                  onChange={(e) => handleInputChange('district_name', e.target.value)}
                  className={errors.district_name ? 'border-red-500' : ''}
                  required
                />
                {errors.district_name && <p className="text-sm text-red-500 mt-1">{errors.district_name}</p>}
              </div>
              
              <div>
                <Label htmlFor="po_number">PO Number *</Label>
                <Input
                  id="po_number"
                  value={formData.po_number}
                  onChange={(e) => handleInputChange('po_number', e.target.value)}
                  className={errors.po_number ? 'border-red-500' : ''}
                  required
                />
                {errors.po_number && <p className="text-sm text-red-500 mt-1">{errors.po_number}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="po_amount">PO Amount *</Label>
              <Input
                id="po_amount"
                value={formData.po_amount}
                onChange={(e) => handleInputChange('po_amount', e.target.value)}
                placeholder="e.g., $25,000 or 25000"
                className={errors.po_amount ? 'border-red-500' : ''}
                required
              />
              {errors.po_amount && <p className="text-sm text-red-500 mt-1">{errors.po_amount}</p>}
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => handleInputChange('contact_name', e.target.value)}
                  className={errors.contact_name ? 'border-red-500' : ''}
                  required
                />
                {errors.contact_name && <p className="text-sm text-red-500 mt-1">{errors.contact_name}</p>}
              </div>
              
              <div>
                <Label htmlFor="contact_email">Contact Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  className={errors.contact_email ? 'border-red-500' : ''}
                  required
                />
                {errors.contact_email && <p className="text-sm text-red-500 mt-1">{errors.contact_email}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                className={errors.contact_phone ? 'border-red-500' : ''}
              />
              {errors.contact_phone && <p className="text-sm text-red-500 mt-1">{errors.contact_phone}</p>}
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <Label htmlFor="comments">Additional Comments</Label>
            <Textarea
              id="comments"
              value={formData.comments}
              onChange={(e) => handleInputChange('comments', e.target.value)}
              rows={3}
              placeholder="Any additional information about your requirements..."
              className={errors.comments ? 'border-red-500' : ''}
            />
            {errors.comments && <p className="text-sm text-red-500 mt-1">{errors.comments}</p>}
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Submit Purchase Order
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}